import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { patchDetails, type MemberRecord } from './api'
import { changedFields, detailsErrorText, formFromRecord, type DetailsForm } from './details'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-3 text-paper disabled:opacity-50 ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
type TextKey = Exclude<keyof DetailsForm, 'do_not_email' | 'postal_renewals'>
const btn = `inline-flex min-h-[40px] items-center rounded-lg border border-ink-400 px-4 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`

export function EditDetails({ m, onSaved, onCancel }: { m: MemberRecord; onSaved: (m: MemberRecord) => void; onCancel: () => void }) {
  const initial = formFromRecord(m)
  const [form, setForm] = useState<DetailsForm>(initial)
  const [error, setError] = useState<string | null>(null)
  const owned = m.sc_owned_identity
  const set = <K extends keyof DetailsForm>(k: K, v: DetailsForm[K]) => setForm((f) => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => patchDetails(m.id, body),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? detailsErrorText(e.status, e.detail) : 'Could not save. Check your connection and try again.'),
  })

  const patch = changedFields(initial, form)
  // While SimpleCirc owns the record only the email and post choices are ours to change.
  const identityTouched = Object.keys(patch).some((k) => k !== 'do_not_email' && k !== 'postal_renewals')
  const blocked = owned && identityTouched
  const countries = ['US', 'CA'].includes(form.country) ? ['US', 'CA'] : [form.country, 'US', 'CA']
  const idDisabled = owned

  const field = (k: TextKey, label: string, extra: { type?: string; wide?: boolean } = {}) => (
    <label className={`${lbl} ${extra.wide ? 'col-span-2' : ''}`}>
      {label}
      <input
        className={inp}
        type={extra.type ?? 'text'}
        value={form[k]}
        disabled={idDisabled}
        onChange={(e) => set(k, e.target.value)}
      />
    </label>
  )

  return (
    <form
      aria-labelledby="edit-h"
      className="flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5"
      onSubmit={(e) => {
        e.preventDefault()
        setError(null)
        if (Object.keys(patch).length === 0) { onCancel(); return }
        save.mutate(patch)
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <h2 id="edit-h" className="font-serif text-[20px]">Edit member details</h2>
        <span className="text-[12px] text-paper/60">Applies to every title they hold. Each change is logged with your name.</span>
      </div>
      {owned && <p role="note" className="m-0 text-[13px] text-amber-glow">Managed in SimpleCirc. Name, contact details and address are edited there; a change here would be overwritten by the next sync.</p>}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5">
        {field('first_name', 'First name')}
        {field('last_name', 'Last name')}
        {field('company', 'Company')}
        {field('email', 'Email', { type: 'email' })}
        {field('phone', 'Phone', { type: 'tel' })}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3.5">
        {field('address_1', 'Street', { wide: true })}
        {field('address_2', 'Street line 2')}
        {field('city', 'City')}
        {field('state', 'State')}
        {field('zipcode', 'ZIP')}
        <label className={lbl}>
          Country
          <select className={inp} value={form.country} disabled={idDisabled} onChange={(e) => set('country', e.target.value)}>
            {countries.map((c) => <option key={c} value={c}>{c === 'US' ? 'United States' : c === 'CA' ? 'Canada' : c}</option>)}
          </select>
        </label>
      </div>
      {m.email_block_reason ? (
        <p className="m-0 text-paper/70">Email is already blocked: {m.email_block_reason}</p>
      ) : (
        <label className="inline-flex min-h-[32px] items-center gap-2 text-paper/70">
          <input type="checkbox" checked={form.do_not_email} onChange={(e) => set('do_not_email', e.target.checked)} />
          Do not email
        </label>
      )}
      <label className="inline-flex min-h-[32px] items-center gap-2 text-paper/70">
        <input type="checkbox" checked={form.postal_renewals} onChange={(e) => set('postal_renewals', e.target.checked)} />
        Send renewal notices by post
      </label>
      <p className="m-0 text-[13px] text-paper/60">Changing the email changes how they sign in. They get a notice at both the old and the new address.</p>
      {error && <p role="alert" className="m-0 text-red-500">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={save.isPending || blocked} className={`inline-flex min-h-[40px] items-center rounded-lg bg-paper px-4 font-bold text-ink-900 disabled:opacity-50 ${focus}`}>
          {save.isPending ? 'Saving…' : 'Save details'}
        </button>
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
